/**
 * RESPONSIBILITY: annotation metadata → JSONForms UI Schema. PRESENTATION only.
 *
 * `options.widget` is the single hint the renderer registry matches on. That is why
 * there is no `switch (kind)` anywhere in the rendering path: a new widget registers
 * a tester and a rank, and this file learns one more string.
 */
import { type FieldMeta, type SectionMeta, rowFields } from '../decorators';
import type { UiSchema } from './types';

/** Widget hint consumed by our custom renderer testers. */
function widgetFor(f: FieldMeta): string | undefined {
  switch (f.kind) {
    case 'color': return 'color';
    case 'img': return 'image';
    case 'video': return 'video';
    case 'list': return 'list';
    case 'paragraph': return 'textarea';
    case 'date': return 'date';
    case 'repeat': return 'repeat';
    case 'stringlist': return 'stringlist';
    // A choice whose vocabulary lives in the document needs a control that can read it.
    // A fixed one is still an enum, which the vanilla select already handles — unless it
    // asked to be drawn as radios or checkboxes, which no vanilla control offers.
    case 'choice':
      if (f.as) return 'choicegroup';
      return f.in || f.multiple ? 'lookup' : undefined;
    default: return undefined;   // text / link / num / bool → vanilla controls
  }
}

/**
 * One @List column, described for the control that draws it.
 *
 * The WHOLE annotation travels with each column, because the list draws its own cells and
 * each cell needs what its own annotation said. Forwarding only kind and maxLength is why
 * an @Img inside a row used to render as a text box: the cell had no folder to upload
 * into, so there was nothing for it to be but text.
 *
 * RECURSIVE. A column that is itself a list carries its own columns, at every depth. The
 * data schema and the validator have always recursed — see json-schema.ts and
 * zod-schema.ts — and only this file did not, so a list inside a row reached the renderer
 * as a column with no columns and fell through to that same text box.
 */
function columnsOf(ctor: Function): Record<string, unknown>[] {
  return rowFields(ctor).filter((x) => !x.derived).map(column);
}

function column(x: FieldMeta): Record<string, unknown> {
  const c: Record<string, unknown> = {
    key: x.key, label: x.label, kind: x.kind,
    maxLength: x.maxLength, maxItems: x.maxItems, minItems: x.minItems,
    rows: x.rows, markdown: x.markdown,
    folder: x.folder, maxSizeMb: x.maxSizeMb, size: x.size,
    time: x.time,
    min: x.min, max: x.max, integer: x.integer,
    in: x.in, multiple: x.multiple, fixed: x.options, as: x.as,
    group: x.group, collapsed: x.collapsed, description: x.description,
    showWhen: x.showWhen,
    // Carried so a row ADDED in the editor starts where a row loaded from the server
    // does. Without it every new row was blank in every column, which for a dropdown
    // meant a value outside its own list.
    default: x.default,
  };
  if (x.kind === 'list' && x.of) c.itemFields = columnsOf(x.of());
  return c;
}

function control(f: FieldMeta, prefix = '#/properties'): UiSchema {
  const options: Record<string, unknown> = {};
  const widget = widgetFor(f);
  if (widget) options.widget = widget;
  if (f.kind === 'paragraph') {
    options.multi = true;
    if (f.rows) options.rows = f.rows;
    // The editor still types plain text; this tells the control to offer a preview and
    // the page to render it rather than print it.
    if (f.markdown) options.markdown = true;
  }
  if (f.kind === 'img' || f.kind === 'video') {
    options.folder = f.folder;
    options.maxSizeMb = f.maxSizeMb;
    options.size = f.size;
  }
  if (f.kind === 'date') options.time = Boolean(f.time);
  if (f.kind === 'choice') {
    if (f.in) options.in = f.in;
    if (f.multiple) options.multiple = true;
    if (f.options) options.fixed = f.options;
    if (f.as) options.as = f.as;
  }
  if (f.kind === 'list' && f.of) options.itemFields = columnsOf(f.of());
  if (f.description) options.description = f.description;

  const ui: UiSchema = { type: 'Control', scope: `${prefix}/${f.key}`, label: f.label };
  if (Object.keys(options).length) ui.options = options;
  return ui;
}

/**
 * Fields declaring the same `group` are collected into one collapsible panel, placed
 * where the first of them was declared. Everything else stays at the top level, so a
 * section that names no group produces exactly the form it always did.
 */
function laidOut(fields: FieldMeta[]): UiSchema[] {
  const elements: UiSchema[] = [];
  const panels = new Map<string, UiSchema>();

  for (const f of fields) {
    if (!f.group) { elements.push(control(f)); continue; }

    let panel = panels.get(f.group);
    if (!panel) {
      panel = { type: 'Group', label: f.group, elements: [] };
      // The field that CREATES the group decides whether it starts open.
      if (f.collapsed) panel.options = { open: false };
      panels.set(f.group, panel);
      elements.push(panel);
    }
    panel.elements!.push(control(f));
  }

  return elements;
}

/**
 * @Derived fields are absent, not disabled. A control that renders a value the server
 * owns invites an editor to change it and then silently discards what they typed.
 */
export const toUiSchema = (section: SectionMeta): UiSchema => ({
  type: 'VerticalLayout',
  elements: laidOut(section.fields.filter((f) => !f.derived)),
});
